import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './UnderConstructionNotice.css';

/**
 * Modal popup that announces a page is still under development. Appears
 * automatically on mount, dismissible via the "Got it" button, the × in
 * the corner, ESC, or clicking the backdrop.
 *
 * Usage:
 *   <UnderConstructionNotice section="Lessons" />
 *   <UnderConstructionNotice section="Business" message="Custom copy…" />
 */
export default function UnderConstructionNotice({ section = 'This section', message }) {
  const [open, setOpen] = React.useState(true);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const defaultMessage = `${section} is currently under development. Existing functionality may be temporarily unavailable or unreliable. We are rebuilding it and will let you know as soon as it is back.`;

  return createPortal(
    <div
      className="ucn-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ucn-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="ucn-modal">
        <div className="ucn-accent" aria-hidden />

        <div className="ucn-icon" aria-hidden>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>

        <h2 id="ucn-title" className="ucn-title">{section}<br /><span className="ucn-subtitle">is under development</span></h2>

        <p className="ucn-text">{message || defaultMessage}</p>

        <button
          type="button"
          className="ucn-cta"
          onClick={() => setOpen(false)}
          autoFocus
        >
          Got it
        </button>

        <p className="ucn-footnote">We&apos;ll let you know as soon as it&apos;s back ✨</p>
      </div>
    </div>,
    document.body
  );
}
